import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { paginateQuery } from '@/lib/paginateSupabase';

export interface ExecutiveDataset {
  profiles: { id: string; organization_id: string | null; created_at: string }[];
  projects: {
    id: string;
    user_id: string;
    cohort_id: string | null;
    status: string | null;
    submitted: boolean | null;
    submitted_at: string | null;
    created_at: string;
    solution_type: number | null;
  }[];
  evaluations: { id: string; project_id: string; created_at: string | null }[];
  streaks: { user_id: string; last_activity_date: string | null; scope_type: string | null }[];
  enrollments: { id: string; status: string | null; created_at: string; cohort_id: string | null }[];
  subscriptions: { id: string; user_id: string; status: string | null; created_at: string }[];
  payments: { id: string; amount: number | null; currency: string | null; status: string | null; created_at: string; organization_id: string | null }[];
  certificates: { id: string; generated_at: string | null; organization_id: string | null }[];
  roi: { project_id: string; cost_subscriptions: number | null; cost_tokens: number | null; cost_personnel: number | null; cost_training: number | null; benefits: any }[];
  organizations: { id: string; name: string; logo_url: string | null; status: string | null; created_at: string }[];
  cohorts: { id: string; name: string; organization_id: string | null; status: string | null; start_date: string | null; end_date: string | null; created_at: string }[];
}

export function useExecutiveMetrics() {
  return useQuery({
    queryKey: ['executive-metrics'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ExecutiveDataset> => {
      const [
        profiles,
        projects,
        evaluations,
        streaks,
        enrollments,
        subscriptions,
        payments,
        certificates,
        roi,
        organizations,
        cohorts,
      ] = await Promise.all([
        paginateQuery<ExecutiveDataset['profiles'][number]>((from, to) =>
          supabase.from('profiles').select('id, organization_id, created_at').range(from, to)
        ),
        paginateQuery<ExecutiveDataset['projects'][number]>((from, to) =>
          supabase.from('participant_projects')
            .select('id, user_id, cohort_id, status, submitted, submitted_at, created_at, solution_type')
            .range(from, to)
        ),
        paginateQuery<ExecutiveDataset['evaluations'][number]>((from, to) =>
          supabase.from('project_evaluations').select('id, project_id, created_at').range(from, to)
        ),
        paginateQuery<ExecutiveDataset['streaks'][number]>((from, to) =>
          supabase.from('participant_streaks').select('user_id, last_activity_date, scope_type').range(from, to)
        ),
        paginateQuery<ExecutiveDataset['enrollments'][number]>((from, to) =>
          supabase.from('cohort_enrollments').select('id, status, created_at, cohort_id').range(from, to)
        ),
        paginateQuery<ExecutiveDataset['subscriptions'][number]>((from, to) =>
          supabase.from('user_subscriptions').select('id, user_id, status, created_at').range(from, to)
        ),
        paginateQuery<ExecutiveDataset['payments'][number]>((from, to) =>
          supabase.from('payment_transactions')
            .select('id, amount, currency, status, created_at, organization_id')
            .range(from, to)
        ),
        paginateQuery<ExecutiveDataset['certificates'][number]>((from, to) =>
          supabase.from('certificate_logs').select('id, generated_at, organization_id').range(from, to)
        ),
        paginateQuery<ExecutiveDataset['roi'][number]>((from, to) =>
          supabase.from('project_roi')
            .select('project_id, cost_subscriptions, cost_tokens, cost_personnel, cost_training, benefits')
            .range(from, to)
        ),
        paginateQuery<ExecutiveDataset['organizations'][number]>((from, to) =>
          supabase.from('organizations').select('id, name, logo_url, status, created_at').range(from, to)
        ),
        paginateQuery<ExecutiveDataset['cohorts'][number]>((from, to) =>
          supabase.from('cohorts').select('id, name, organization_id, status, start_date, end_date, created_at').eq('is_perpetual', false).range(from, to)
        ),
      ]);

      return {
        profiles,
        projects,
        evaluations,
        streaks,
        enrollments,
        subscriptions,
        payments,
        certificates,
        roi,
        organizations,
        cohorts,
      };
    },
  });
}
